import { ArrowRightOutlined, InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { Button, Card, Col, Descriptions, Flex, Grid, Modal, Popover, Row, Table, Tag } from "antd";
import { DATE_FORMAT } from "common/constants";
import { formatCurrency, insertIf } from "common/utils";
import currency from "currency.js";
import { useGetConsumerPortalAccountsQuery } from "features/consumerPortalDashboard/accountsAPI";
import {
  PAYMENT_VIEW_SCREEN_ENUMS,
  selectPaymentsSlice,
  setCurrentView,
  setSelectedAccounts,
} from "features/consumerPortalPaymentPage/paymentsSlice";
import { useFetchPaymentIntentsQuery } from "features/consumerPortalUpcomingPayments/paymentIntentsAPI";
import useIsMobile from "hooks/useIsMobile";
import Content from "layouts/consumerPortal/content";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

const StyledDiv = styled.div`
  margin-top: 24px;
  margin-left: 16px;
  & span {
    margin-left: 12px;
  }
`;
const StyledInfoValue = styled.div`
  margin-left: 4px;
  margin-right: 4px;
`;
const StyledButton = styled(Button)`
  margin-top: 24px;
  float: right;
`;

const StyledCard = styled(Card)`
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 0 0 16px;
    }
  }
`;

const ViewDetailsLink = styled.a`
  color: #1890ff;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }
`;

const BreakAllSpan = styled.span`
  word-break: break-all;
`;

function BalanceDetailsPopoverOrModal({ balanceItems, isMobile }) {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const totalBalanceContent = (
    <Col>
      <Descriptions bordered column={1} size="small">
        {balanceItems.map((item) => (
          <Descriptions.Item key={item.key} label={item.label}>
            {item.value}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Col>
  );

  if (isMobile) {
    return (
      <>
        <ViewDetailsLink onClick={handleOpenModal}>View Details</ViewDetailsLink>
        <Modal
          title="Current Balance Breakdown"
          open={isModalVisible}
          onCancel={handleCloseModal}
          footer={null}
        >
          {totalBalanceContent}
        </Modal>
      </>
    );
  }

  return (
    <Popover
      content={totalBalanceContent}
      title="Current Balance Breakdown"
      trigger="click"
      placement="right"
    >
      <InfoCircleOutlined />
    </Popover>
  );
}

function SelectAccounts() {
  const { data: accounts, isLoading } = useGetConsumerPortalAccountsQuery();
  const dispatch = useDispatch();
  const paymentsSlice = useSelector(selectPaymentsSlice);
  const isMobile = useIsMobile();
  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const { data: scheduledPayments = [] } = useFetchPaymentIntentsQuery({
    scheduledDateStart: moment().add("day", 1).format(DATE_FORMAT),
  });
  const filteredScheduledPayments = scheduledPayments.filter(
    (account) =>
      moment(account.scheduledDate).isSameOrAfter(moment(), "day") && !account.executedAt,
  );

  const onSelectedAccountChange = (_, selectedAccounts) => {
    dispatch(setSelectedAccounts(selectedAccounts));
  };

  const rowSelection = {
    selectedRowKeys: paymentsSlice.selectedAccounts.map((row) => row.uuid),
    onChange: onSelectedAccountChange,
    preserveSelectedRowKeys: true,
  };

  const onContinue = () => {
    dispatch(setCurrentView(PAYMENT_VIEW_SCREEN_ENUMS.SelectPaymentOption));
  };

  const columns = [
    { title: "Creditor", dataIndex: "currentCreditorName", key: "currentCreditorName" },
    {
      title: "Account Number",
      dataIndex: "externalId",
      key: "externalId",
      render: (text, record) => {
        return filteredScheduledPayments?.find((each) =>
          each.accountExternalIds.includes(record.externalId.toLowerCase()),
        ) ? (
          <Flex gap={4} wrap="wrap">
            <BreakAllSpan>{text}</BreakAllSpan>
            {/* Using breakpoint.xs to render "SCHEDULED" in screens below 576px */}
            <Tag color="yellow">{breakpoints.xs ? "SCHEDULED" : "PAYMENT(S) SCHEDULED"}</Tag>
          </Flex>
        ) : (
          <BreakAllSpan>{text}</BreakAllSpan>
        );
      },
    },
    {
      title: "Current Balance",
      dataIndex: "totalBalance",
      key: "totalBalance",
      render: (text, record) => {
        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(record.principalBalance),
          },
          {
            key: "interest",
            label: `Agency Interest (${
              currency(record.interestRate, { precision: 4 }).multiply(100).value
            }%)`,
            value: formatCurrency(record.interestBalance),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(record.clientInterestBalance),
          },
          ...insertIf(record.legalStatus === "judgment", {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(record.prejudgmentInterestBalance),
          }),
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(record.totalFeesBalance),
          },
          {
            key: "totalAmountPaid",
            label: "Total Amount Paid",
            value: formatCurrency(record.totalAmountPaid),
          },
          // {
          //   key: "originalTotal",
          //   label: <strong>Total</strong>,
          //   value: <strong>{formatCurrency(text)}</strong>,
          // },
        ];
        return (
          <Row align="middle">
            <StyledInfoValue>{formatCurrency(text)}</StyledInfoValue>
            <BalanceDetailsPopoverOrModal balanceItems={balanceItems} isMobile={isMobile} />
          </Row>
        );
      },
    },
  ];

  return (
    <Content>
      <h3>Payment Accounts</h3>

      <StyledCard>
        <Table
          pagination={false}
          // Only show non-$0, active (not closed) accounts.
          // NOTE: Accounts are marked as $0 balance on the backend when they are closed / settled / paid in full.
          dataSource={accounts?.filter(
            (account) => currency(account.totalBalance, { precision: 2 }).value > 0,
          )}
          columns={columns}
          loading={isLoading}
          rowSelection={rowSelection}
          rowKey="uuid"
        />
        <StyledDiv>
          <strong>Total Balance Due</strong>
          <span>{currency(paymentsSlice.originalTotalAmount, { precision: 2 }).format()}</span>
        </StyledDiv>
      </StyledCard>
      <StyledButton
        type="primary"
        icon={<ArrowRightOutlined />}
        onClick={onContinue}
        disabled={paymentsSlice.selectedAccounts.length <= 0}
      >
        Next: Choose Payment Option
      </StyledButton>
    </Content>
  );
}

export default SelectAccounts;
